// Unit selector component for issue creation
// Reference: spec.md US1 - select unit for issue
// Supports both leaseholders (direct unit assignment) and managers/owners (property → unit selection)

import { useState, useEffect } from 'react';
import { Select, FormField } from '../ui';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import type { UnitAssignmentWithUnit, Property } from '@propman/shared';

export interface UnitSelectorProps {
  value: string;
  onChange: (unitId: string) => void;
  error?: string | undefined;
  required?: boolean | undefined;
}

// Property with units for managers/owners
interface PropertyWithUnits extends Property {
  units: Array<{
    id: string;
    identifier: string;
    floor: string | null;
    isCommonArea: boolean;
  }>;
}

export function UnitSelector({ value, onChange, error, required }: UnitSelectorProps) {
  const { user } = useAuth();
  const isManagerOrOwner = user?.role === 'manager' || user?.role === 'owner';

  // For leaseholders: units they are assigned to
  const [units, setUnits] = useState<UnitAssignmentWithUnit[]>([]);

  // For managers/owners: properties with units
  const [properties, setProperties] = useState<PropertyWithUnits[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        setLoadError(null);

        if (isManagerOrOwner) {
          // Managers/owners: fetch properties with units
          const response = await api.get<{ properties: PropertyWithUnits[] }>('/auth/me/properties');
          setProperties(response.properties);
        } else {
          // Leaseholders: fetch assigned units
          const response = await api.get<{ units: UnitAssignmentWithUnit[] }>('/auth/me/units');
          setUnits(response.units);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setLoadError(isManagerOrOwner ? 'Failed to load properties' : 'Failed to load your units');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [isManagerOrOwner]);

  // When property selection changes, clear unit selection
  const handlePropertyChange = (propertyId: string) => {
    setSelectedPropertyId(propertyId);
    onChange(''); // Clear unit selection when property changes
  };

  // Get units for selected property (for managers/owners)
  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const availableUnits = selectedProperty?.units || [];

  // Group leaseholder units by property for better UX
  const groupedUnits = units.reduce<Record<string, UnitAssignmentWithUnit[]>>((acc, assignment) => {
    const propertyName = assignment.unit.property.name;
    if (!acc[propertyName]) {
      acc[propertyName] = [];
    }
    acc[propertyName].push(assignment);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <FormField label="Unit/Location" htmlFor="unit" required={required}>
        <Select id="unit" disabled>
          <option>Loading...</option>
        </Select>
      </FormField>
    );
  }

  if (loadError) {
    return (
      <FormField label="Unit/Location" htmlFor="unit" error={loadError} required={required}>
        <Select id="unit" disabled error>
          <option>Error loading data</option>
        </Select>
      </FormField>
    );
  }

  // Manager/Owner view: Property → Unit selection
  if (isManagerOrOwner) {
    if (properties.length === 0) {
      return (
        <FormField
          label="Property"
          htmlFor="property"
          error="No properties available. Please contact your administrator."
          required={required}
        >
          <Select id="property" disabled>
            <option>No properties available</option>
          </Select>
        </FormField>
      );
    }

    return (
      <div className="space-y-4">
        {/* Property Selection */}
        <FormField
          label="Property"
          htmlFor="property"
          required={required}
          hint="Select the property where the issue is located"
        >
          <Select
            id="property"
            value={selectedPropertyId}
            onChange={(e) => handlePropertyChange(e.target.value)}
            required={required}
          >
            <option value="">Select a property...</option>
            {properties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name}
                {property.units.length > 0 ? ` (${property.units.length} units)` : ''}
              </option>
            ))}
          </Select>
        </FormField>

        {/* Unit Selection - only show after property is selected */}
        {selectedPropertyId && (
          <FormField
            label="Unit/Location"
            htmlFor="unit"
            error={error}
            required={required}
            hint="Select the specific unit or area"
          >
            <Select
              id="unit"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              error={!!error}
              required={required}
            >
              <option value="">Select a unit...</option>
              {availableUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.identifier}
                  {unit.floor ? ` (Floor ${unit.floor})` : ''}
                  {unit.isCommonArea ? ' - Common Area' : ''}
                </option>
              ))}
            </Select>
          </FormField>
        )}
      </div>
    );
  }

  // Leaseholder view: Direct unit selection (grouped by property)
  if (units.length === 0) {
    return (
      <FormField
        label="Unit/Location"
        htmlFor="unit"
        error="No units assigned to your account"
        required={required}
      >
        <Select id="unit" disabled>
          <option>No units available</option>
        </Select>
      </FormField>
    );
  }

  return (
    <FormField
      label="Unit/Location"
      htmlFor="unit"
      error={error}
      required={required}
      hint="Select the unit or area where the issue is located"
    >
      <Select
        id="unit"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={!!error}
        required={required}
      >
        <option value="">Select a unit...</option>
        {Object.entries(groupedUnits).map(([propertyName, assignments]) => (
          <optgroup key={propertyName} label={propertyName}>
            {assignments.map((assignment) => (
              <option key={assignment.unit.id} value={assignment.unit.id}>
                {assignment.unit.identifier}
                {assignment.unit.floor ? ` (${assignment.unit.floor})` : ''}
                {assignment.unit.isCommonArea ? ' - Common Area' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
    </FormField>
  );
}

export default UnitSelector;
